import { Component, OnInit } from '@angular/core';
import { BPProfilesService, IStatusIconLegend } from '../../bp-profiles.service';
import { BPProfilesChecklistsDataService, IProfileChecklist, ProfileChecklistStatus } from './bp-profiles-checklists-data.service';
import * as _ from 'underscore';
import { AuthService } from '../../../../shared/services/auth/auth.service';

@Component({
  selector: 'bp-profile-checklists',
  template: `
    <div class="d-block">
      <info-link [(linkVariable)]="iconInfoToggle">{{ 'BPPROFILES.STATUS_ICON_INFORMATION' | translate }}</info-link>

      <div class="d-block margin-top-xs" *ngIf="iconInfoToggle">
        <ul class="status-icon-legend">
          <li *ngFor="let iconLegend of iconLegends">
            <i [ngClass]="iconLegend.iconClass"></i>
            <span class="margin-left-sm">{{ iconLegend.label | translate }}</span>
          </li>
        </ul>
      </div>
    </div>
    
    <h2 *ngIf="loading"><activity-indicator></activity-indicator></h2>
    
    <div class="profile-checklists-table" *ngIf="!loading">
      <table class="table">
        <tbody>
          <ng-container *ngFor="let checklist of profileChecklists">
            <tr class="profile-checklist-row">
              <td class="col-md-1">
                <i *ngIf="checklist.statusIcon" [ngClass]="checklist.statusIcon.iconClass" [title]="checklist.statusIcon.label"></i>
              </td>
              <td class="col-md-6">
                <b>
                  <info-link *ngIf="checklist.description.linkedContent" [(linkVariable)]="checklist.description.linkedContentToggle">{{checklist.description.displayText}}</info-link>
                  <a *ngIf="checklist.description.externalUrl" [href]="checklist.description.externalUrl" target="_blank">{{checklist.description.displayText}}</a>
                  <span *ngIf="!checklist.description.linkedContent && !checklist.description.externalUrl">{{checklist.description.displayText}}</span>
                </b>
              </td>
              <td class="col-md-5">
                <a *ngIf="checklist.itemValue.internalUrl" [uiSref]="checklist.itemValue.internalUrl.state" [uiParams]="checklist.itemValue.internalUrl.params">{{checklist.itemValue.displayText}}</a>
                <a *ngIf="checklist.itemValue.externalUrl" [href]="checklist.itemValue.externalUrl" target="_blank">{{checklist.itemValue.displayText}}</a>
                <span *ngIf="!checklist.itemValue.internalUrl && !checklist.itemValue.externalUrl">{{checklist.itemValue.displayText}}</span>
              </td>
            </tr>
            <!-- extra info row-->
            <tr class="extra-info-row" *ngIf="checklist.extraInformation">
              <td class="no-border"></td>
              <td class="no-border" colspan="2">
                <a *ngIf="checklist.extraInformation.externalUrl" [href]="checklist.extraInformation.externalUrl" target="_blank">{{checklist.extraInformation.displayText}}</a>
                <b *ngIf="!checklist.extraInformation.externalUrl">{{checklist.extraInformation.displayText}}</b>
                <div *ngIf="checklist.extraInformation.linkedContent">{{checklist.extraInformation.linkedContent}}</div>
              </td>
            </tr>
            <!-- extra links row -->
            <tr class="extra-links-row" *ngIf="checklist.itemValue.linkedContent">
              <td class="no-border"></td>
              <td class="no-border" colspan="2">{{checklist.itemValue.linkedContent}}</td>
            </tr>
            <!-- info linked content row -->
            <tr class="info-row" *ngIf="checklist.description?.linkedContentToggle">
              <td class="info no-border"></td>
              <td class="info no-border" colspan="2">{{checklist.description.linkedContent}}</td>
            </tr>
          </ng-container>
        </tbody>
      </table>
    </div>
  `,
  styleUrls: ['./bp-profiles-checklists.scss']
})
export class BPProfilesChecklistsComponent implements OnInit {
  public loading:boolean;
  public iconInfoToggle:boolean = false;
  public iconLegends:IStatusIconLegend[] = this.bpProfilesChecklistsDataService.statusIconLegends;
  public profileChecklists:IProfileChecklist[];

  constructor(
    private bpProfilesChecklistsDataService:BPProfilesChecklistsDataService,
    private bpProfilesService:BPProfilesService,
    private authService:AuthService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.loading = true;
    this.bpProfilesChecklistsDataService.getProfileChecklists(this.bpProfilesService.activeProfile?.id)
      .then((res:IProfileChecklist[]) => {
        this.profileChecklists = res;
        // process profile checklists
        _.forEach(this.profileChecklists, (profileChecklist:IProfileChecklist) => {
          this.processStatus(profileChecklist);
          this.processLinks(profileChecklist);

          if (profileChecklist.description?.linkedContent) {
            profileChecklist.description.linkedContentToggle = false;
          }
        });
      })
      .finally(() => {
        this.loading = false;
      });
  }

  /**
   * @name processStatus
   * @description turns status info into icon info
   * @params {IProfileChecklist} profileChecklist - checklist object containing a status value
   */
  private processStatus(profileChecklist:IProfileChecklist):void {
    switch (profileChecklist.status) {
      case ProfileChecklistStatus.AVAILABLE:
        profileChecklist.statusIcon = this.bpProfilesChecklistsDataService.statusIconLegends[0];
        break;
      case ProfileChecklistStatus.OPTIONAL_NOT_AVAILABLE:
        profileChecklist.statusIcon = this.bpProfilesChecklistsDataService.statusIconLegends[1];
        break;
      case ProfileChecklistStatus.MANDATORY_NOT_AVAILABLE:
        profileChecklist.statusIcon = this.bpProfilesChecklistsDataService.statusIconLegends[2];
        break;
      default:
        break;
    }
  }

  /**
   * @name processLinks
   * @description turns itemValue in a link (if possible)
   * @params {IProfileChecklist} profileChecklist - checklist object containing a status value
   */
  private processLinks(profileChecklist:IProfileChecklist):void {
    if (profileChecklist.symbolicName && this.bpProfilesChecklistsDataService.internalLinks[ profileChecklist.symbolicName ]) {
      profileChecklist.itemValue.internalUrl = this.bpProfilesChecklistsDataService.internalLinks[ profileChecklist.symbolicName ];
    }

    if (profileChecklist.description?.linkedUrl) {
      profileChecklist.description.externalUrl = this.authService.authenticatedUrl(profileChecklist.description.linkedUrl);
    }

    if (profileChecklist.itemValue?.linkedUrl) {
      profileChecklist.itemValue.externalUrl = this.authService.authenticatedUrl(profileChecklist.itemValue.linkedUrl);
    }

    if (profileChecklist.extraInformation?.linkedUrl) {
      profileChecklist.extraInformation.externalUrl = this.authService.authenticatedUrl(profileChecklist.extraInformation.linkedUrl);
    }
  }
}